import type { DesignSpec, Overlay, TextBlock } from '@/types';
import { FONT_STACK, ensureFontsLoaded, nearestWeight } from '@/lib/fonts';
import { rgba } from '@/lib/color';
import { clamp } from '@/lib/utils';
import { applyTransform, linesToRuns, type StyledRun } from '../typography/textOptimizer';

/**
 * Canvas renderer.
 *
 * The same function draws the on-screen preview and the exported file — only
 * the pixel dimensions differ. That is what guarantees "what you see is what
 * you download"; there is no second layout path that could drift.
 */

export interface RenderOptions {
  width: number;
  height: number;
  /** Draw the photo. False produces a transparent-background export. */
  includeImage: boolean;
  /** Preview-only overlays: safe area, text bounds, detected faces. */
  guides?: {
    safe?: { top: number; bottom: number; left: number; right: number };
    faces?: { x: number; y: number; w: number; h: number }[];
    selectedBlockId?: string | null;
  };
}

/** `ctx.letterSpacing` is widely but not universally supported. */
const SUPPORTS_LETTER_SPACING = (() => {
  if (typeof document === 'undefined') return false;
  const ctx = document.createElement('canvas').getContext('2d');
  return !!ctx && 'letterSpacing' in ctx;
})();

function setFont(
  ctx: CanvasRenderingContext2D,
  weight: number,
  sizePx: number,
  trackingEm: number,
  family: string,
): void {
  ctx.font = `${nearestWeight(weight)} ${sizePx}px ${family || FONT_STACK}`;
  if (SUPPORTS_LETTER_SPACING) {
    (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing =
      `${trackingEm * sizePx}px`;
  }
}

function clearLetterSpacing(ctx: CanvasRenderingContext2D): void {
  if (SUPPORTS_LETTER_SPACING) {
    (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = '0px';
  }
}

/** Width of a string including tracking, whichever spacing path is in use. */
function measure(
  ctx: CanvasRenderingContext2D,
  text: string,
  trackingPx: number,
): number {
  const base = ctx.measureText(text).width;
  // The native path already includes tracking (and adds a trailing advance we
  // subtract so right/center alignment is not skewed).
  return SUPPORTS_LETTER_SPACING ? base - trackingPx : base + text.length * trackingPx;
}

/** Manual per-character draw, used only when ctx.letterSpacing is missing. */
function drawTracked(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  trackingPx: number,
): void {
  let cursor = x;
  for (const ch of text) {
    ctx.fillText(ch, cursor, y);
    cursor += ctx.measureText(ch).width + trackingPx;
  }
}

function drawRuns(
  ctx: CanvasRenderingContext2D,
  runs: StyledRun[],
  startX: number,
  y: number,
  block: TextBlock,
  sizePx: number,
  trackingPx: number,
  family: string,
): void {
  let cursor = startX;
  for (const run of runs) {
    const weight = run.emphasized && block.emphasis ? block.emphasis.weight : block.weight;
    setFont(ctx, weight, sizePx, trackingPx / sizePx, family);
    ctx.fillStyle =
      run.emphasized && block.emphasis ? block.emphasis.color : block.color;

    if (SUPPORTS_LETTER_SPACING) {
      ctx.fillText(run.text, cursor, y);
    } else {
      drawTracked(ctx, run.text, cursor, y, trackingPx);
    }
    cursor += measure(ctx, run.text, trackingPx);
    // Space between runs, drawn in the base font so the gap stays consistent.
    if (run !== runs[runs.length - 1]) {
      setFont(ctx, block.weight, sizePx, trackingPx / sizePx, family);
      cursor += measure(ctx, ' ', trackingPx);
    }
  }
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/** Cover-fit: fill the frame, crop the overflow, honour pan and zoom. */
function drawPhoto(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  spec: DesignSpec,
  W: number,
  H: number,
): void {
  const { image } = spec;
  const filters = [
    `brightness(${image.brightness})`,
    `contrast(${image.contrast})`,
    `saturate(${image.saturation})`,
  ];
  if (image.blur > 0) filters.push(`blur(${image.blur * W}px)`);
  ctx.filter = filters.join(' ');

  const scale = Math.max(W / img.naturalWidth, H / img.naturalHeight) * image.scale;
  const dw = img.naturalWidth * scale;
  const dh = img.naturalHeight * scale;
  const dx = (W - dw) / 2 + image.offsetX * W;
  const dy = (H - dh) / 2 + image.offsetY * H;
  ctx.drawImage(img, dx, dy, dw, dh);
  ctx.filter = 'none';
}

function drawOverlay(
  ctx: CanvasRenderingContext2D,
  ov: Overlay,
  img: HTMLImageElement | null,
  spec: DesignSpec,
  W: number,
  H: number,
): void {
  if (!ov.visible) return;

  if (ov.kind === 'solid') {
    ctx.fillStyle = rgba(ov.color, ov.opacity);
    ctx.fillRect(0, 0, W, H);
    return;
  }

  if (ov.kind === 'linear') {
    const rad = (ov.angle * Math.PI) / 180;
    const cx = W / 2;
    const cy = H / 2;
    const len = Math.abs(W * Math.cos(rad)) + Math.abs(H * Math.sin(rad));
    const gx = (Math.cos(rad) * len) / 2;
    const gy = (Math.sin(rad) * len) / 2;
    const grad = ctx.createLinearGradient(cx - gx, cy - gy, cx + gx, cy + gy);
    for (const stop of ov.stops) {
      grad.addColorStop(clamp(stop.at, 0, 1), rgba(stop.color, stop.alpha * ov.opacity));
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
    return;
  }

  if (ov.kind === 'radial') {
    const grad = ctx.createRadialGradient(
      ov.cx * W,
      ov.cy * H,
      0,
      ov.cx * W,
      ov.cy * H,
      ov.r * W,
    );
    for (const stop of ov.stops) {
      grad.addColorStop(clamp(stop.at, 0, 1), rgba(stop.color, stop.alpha * ov.opacity));
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
    return;
  }

  // Panel — optionally frosted. Canvas has no backdrop-filter, so we re-draw
  // the photo through a blur filter and clip it to the panel shape.
  const x = ov.rect.x * W;
  const y = ov.rect.y * H;
  const w = ov.rect.w * W;
  const h = ov.rect.h * H;
  const r = ov.radius * W;

  ctx.save();
  roundRectPath(ctx, x, y, w, h, r);
  ctx.clip();

  if (ov.blur > 0 && img) {
    ctx.filter = `blur(${ov.blur * W}px)`;
    const scale = Math.max(W / img.naturalWidth, H / img.naturalHeight) * spec.image.scale;
    const dw = img.naturalWidth * scale;
    const dh = img.naturalHeight * scale;
    ctx.drawImage(
      img,
      (W - dw) / 2 + spec.image.offsetX * W,
      (H - dh) / 2 + spec.image.offsetY * H,
      dw,
      dh,
    );
    ctx.filter = 'none';
  }

  ctx.fillStyle = rgba(ov.color, ov.opacity);
  ctx.fillRect(x, y, w, h);
  ctx.restore();

  if (ov.border) {
    ctx.save();
    ctx.strokeStyle = rgba(ov.border.color, ov.border.alpha);
    ctx.lineWidth = Math.max(1, ov.border.width * W);
    roundRectPath(ctx, x, y, w, h, r);
    ctx.stroke();
    ctx.restore();
  }
}

/** Total drawn height of a block, in pixels. Also used for hit-testing. */
export function blockHeightPx(block: TextBlock, W: number): number {
  const size = block.fontSize * W;
  return block.lines.length * block.lineHeight * size;
}

export function blockBoundsPx(
  block: TextBlock,
  W: number,
  H: number,
): { x: number; y: number; w: number; h: number } {
  const w = block.width * W;
  const h = blockHeightPx(block, W);
  const left =
    block.align === 'center'
      ? block.x * W - w / 2
      : block.align === 'right'
        ? block.x * W - w
        : block.x * W;
  let top = block.y * H;
  if (block.anchorV === 'middle') top -= h / 2;
  else if (block.anchorV === 'bottom') top -= h;
  return { x: left, y: top, w, h };
}

function drawTextBlock(
  ctx: CanvasRenderingContext2D,
  block: TextBlock,
  W: number,
  H: number,
): void {
  if (!block.visible || block.lines.length === 0) return;

  const sizePx = block.fontSize * W;
  const trackingPx = block.tracking * sizePx;
  const lineHeightPx = block.lineHeight * sizePx;
  const family = block.fontFamily || FONT_STACK;

  ctx.save();
  ctx.globalAlpha = block.opacity;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';

  if (block.shadow) {
    ctx.shadowColor = rgba(block.shadow.color, block.shadow.alpha);
    ctx.shadowBlur = block.shadow.blur * W;
    ctx.shadowOffsetX = block.shadow.x * W;
    ctx.shadowOffsetY = block.shadow.y * W;
  }

  const bounds = blockBoundsPx(block, W, H);
  const runsPerLine = block.emphasis
    ? linesToRuns(block.lines, block.emphasis.words)
    : block.lines.map((l) => [{ text: l, emphasized: false }]);

  block.lines.forEach((line, i) => {
    const displayed = applyTransform(line, block.transform);
    setFont(ctx, block.weight, sizePx, block.tracking, family);
    const lineWidth = measure(ctx, displayed, trackingPx);

    let x = bounds.x;
    if (block.align === 'center') x = bounds.x + (bounds.w - lineWidth) / 2;
    else if (block.align === 'right') x = bounds.x + bounds.w - lineWidth;

    // Baseline sits ~0.76 down the line box, which visually centers caps and
    // lowercase together for a sans of these proportions.
    const y = bounds.y + i * lineHeightPx + sizePx * 0.76;

    const runs = runsPerLine[i].map((r) => ({
      ...r,
      text: applyTransform(r.text, block.transform),
    }));

    if (runs.length === 1 && !runs[0].emphasized) {
      ctx.fillStyle = block.color;
      if (SUPPORTS_LETTER_SPACING) ctx.fillText(displayed, x, y);
      else drawTracked(ctx, displayed, x, y, trackingPx);
    } else {
      drawRuns(ctx, runs, x, y, block, sizePx, trackingPx, family);
    }
  });

  clearLetterSpacing(ctx);
  ctx.restore();
}

function drawLogo(
  ctx: CanvasRenderingContext2D,
  spec: DesignSpec,
  logoImg: HTMLImageElement,
  W: number,
  H: number,
): void {
  const logo = spec.logo;
  if (!logo || !logo.visible) return;

  const w = logo.width * W;
  const h = (logoImg.naturalHeight / logoImg.naturalWidth) * w;

  let x = logo.x * W;
  let y = logo.y * H;
  if (!logo.freePosition) {
    x = logo.corner.endsWith('right') ? W - logo.padding * W - w : logo.padding * W;
    y = logo.corner.startsWith('bottom') ? H - logo.padding * H - h : logo.padding * H;
  }

  ctx.save();
  ctx.globalAlpha = logo.opacity;
  ctx.drawImage(logoImg, x, y, w, h);
  ctx.restore();
}

function drawDecor(ctx: CanvasRenderingContext2D, spec: DesignSpec, W: number, H: number): void {
  for (const d of spec.decor) {
    if (!d.visible) continue;
    ctx.save();
    ctx.globalAlpha = d.opacity;
    ctx.fillStyle = d.color;
    if (d.kind === 'rule') {
      ctx.fillRect(d.x * W, d.y * H, d.length * W, Math.max(1, d.thickness * W));
    } else {
      ctx.fillRect(d.rect.x * W, d.rect.y * H, d.rect.w * W, d.rect.h * H);
    }
    ctx.restore();
  }
}

function drawGuides(
  ctx: CanvasRenderingContext2D,
  spec: DesignSpec,
  options: RenderOptions,
  W: number,
  H: number,
): void {
  const g = options.guides;
  if (!g) return;

  ctx.save();
  ctx.setLineDash([W * 0.008, W * 0.008]);
  ctx.lineWidth = Math.max(1, W * 0.0015);

  if (g.safe) {
    ctx.strokeStyle = 'rgba(120, 200, 255, 0.55)';
    ctx.strokeRect(
      g.safe.left * W,
      g.safe.top * H,
      W - (g.safe.left + g.safe.right) * W,
      H - (g.safe.top + g.safe.bottom) * H,
    );
  }

  if (g.faces?.length) {
    ctx.strokeStyle = 'rgba(255, 118, 118, 0.85)';
    ctx.setLineDash([]);
    for (const f of g.faces) {
      ctx.strokeRect(f.x * W, f.y * H, f.w * W, f.h * H);
    }
  }

  if (g.selectedBlockId) {
    const block = spec.blocks.find((b) => b.id === g.selectedBlockId);
    if (block) {
      const b = blockBoundsPx(block, W, H);
      ctx.setLineDash([]);
      ctx.strokeStyle = 'rgba(200, 168, 130, 0.95)';
      ctx.strokeRect(b.x, b.y, b.w, b.h);
      // Resize handle at the lower-right of the block.
      ctx.fillStyle = 'rgba(200, 168, 130, 0.95)';
      const hs = Math.max(6, W * 0.012);
      ctx.fillRect(b.x + b.w - hs / 2, b.y + b.h - hs / 2, hs, hs);
    }
  }

  ctx.restore();
}

export async function renderDesign(
  ctx: CanvasRenderingContext2D,
  spec: DesignSpec,
  photo: HTMLImageElement | null,
  logoImg: HTMLImageElement | null,
  options: RenderOptions,
): Promise<void> {
  const { width: W, height: H } = options;

  await ensureFontsLoaded([
    ...spec.blocks.map((b) => b.weight),
    ...spec.blocks.flatMap((b) => (b.emphasis ? [b.emphasis.weight] : [])),
  ]);

  ctx.clearRect(0, 0, W, H);

  if (options.includeImage && photo) {
    drawPhoto(ctx, photo, spec, W, H);
  } else if (options.includeImage) {
    // No photo yet — a neutral field so the type is still evaluable.
    ctx.fillStyle = '#1a1a20';
    ctx.fillRect(0, 0, W, H);
  }

  for (const ov of spec.overlays) {
    drawOverlay(ctx, ov, options.includeImage ? photo : null, spec, W, H);
  }

  drawDecor(ctx, spec, W, H);

  for (const block of spec.blocks) {
    drawTextBlock(ctx, block, W, H);
  }

  if (logoImg && spec.logo) drawLogo(ctx, spec, logoImg, W, H);

  drawGuides(ctx, spec, options, W, H);
}

/** Render to a detached canvas — used for exports and thumbnails. */
export async function renderToCanvas(
  spec: DesignSpec,
  photo: HTMLImageElement | null,
  logoImg: HTMLImageElement | null,
  width: number,
  height: number,
  includeImage = true,
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D is unavailable in this browser.');
  await renderDesign(ctx, spec, photo, logoImg, { width, height, includeImage });
  return canvas;
}
